import {
  BaseModel,
  DebouncedWatchable,
  TargetableModel,
  useInScopeModel,
  Watchable,
} from '@cotera/client/v0/actions/framework';
import {
  type ReadonlyDebouncedWatchable,
  type ReadonlyEventWatchable,
  type ReadonlyWatchable,
} from '@cotera/client/v0/actions/framework/watchable';
import type { QueryClient } from '@tanstack/react-query';
import type { UrlParamAdapter } from '@cotera/client/app/etc/url-param-watchable';
import { MultiKeyUrlParamWatchable } from '@cotera/client/app/etc/url-param-watchable';
import {
  RESOURCE_LIST_FETCH_STALE_MS,
  queryKeys,
} from '@cotera/client/app/hooks/query-cache-keys';
import { ResourceType } from '@cotera/api';
import { ALL_RESOURCE_KINDS } from '@cotera/sdk/core';
import type {
  TenantedClient,
  FolderItemsResponse,
} from '@cotera/client/app/etc/api/typed-client';
import { Assert } from '@cotera/utilities';
import { WatchablesFactory } from '../watchables/watchables-factory';
import {
  folderContentsQueryKey,
  type FolderContentsQuery,
} from './folder-item-count';
import { DEFAULT_CHAT_HISTORY_TRIGGER_SOURCES } from '../chat/utils/chat-trigger-source';

export type FolderContentItem = FolderItemsResponse['items'][number];

/**
 * Aggregate behind a folder's collapsed "Chats" row: the true
 * (trigger-source-filtered) chat count and the newest chat's `updatedAt` for the
 * row's timestamp.
 */
export type FolderChatSummary = {
  count: number;
  newestUpdatedAt: string | null;
};

/**
 * Folder-content types shown individually in the folder view — every type
 * except chats. Chats collapse into a single "Chats" summary row, so fetching
 * them alongside artifacts lets a chatty folder's chats crowd real artifacts
 * out of the (paginated) fetch window and make them vanish from the list
 * (COT-6561). We fetch non-chat artifacts and the chat summary separately.
 */
const NON_CHAT_FOLDER_CONTENT_TYPES: ResourceType[] = Object.values(
  ResourceType
).filter((t) => t !== ResourceType.Chat);

/** Single place to get id from any folder content item; id is always at .data.id. */
export function getFolderContentItemId(item: FolderContentItem): string {
  return (item.data as { id: string }).id;
}

export type ListParamsState = {
  page: number;
  limit: number;
  search: string;
  sort: string | undefined;
  sortDir: 'asc' | 'desc';
  scope: 'private' | 'shared' | undefined;
  /** Which resource type the Library is filtered to. `all` = folders + unfiled artifacts. */
  type: 'all' | 'agent' | 'document' | 'workflow' | 'dataset';
  /** Ownership lens. `all` = everything accessible; `mine` = owned by me; `shared` = external inbound shares. */
  ownership: 'all' | 'mine' | 'shared';
  /** Rows vs cards. URL-persisted so the choice survives reload and sharing. */
  view: LibraryViewMode;
};

export type LibraryViewMode = 'list' | 'grid';

/**
 * How long typing must pause before a search commits. A commit is a server
 * round trip AND a history rewrite, so it has to trail typing rather than
 * follow it per keystroke.
 */
const SEARCH_DEBOUNCE_MS = 250;

export const DEFAULT_FOLDER_LIST_PARAMS: ListParamsState = {
  page: 1,
  limit: 100,
  search: '',
  sort: undefined,
  sortDir: 'desc',
  scope: undefined,
  type: 'all',
  ownership: 'all',
  view: 'list',
};

function parsePage(s: string): number {
  if (s === '' || s === null || s === undefined) {
    return DEFAULT_FOLDER_LIST_PARAMS.page;
  }
  const n = Number(s);
  return Number.isFinite(n) && n >= 1
    ? Math.floor(n)
    : DEFAULT_FOLDER_LIST_PARAMS.page;
}

function parseLimit(s: string): number {
  if (s === '' || s === null || s === undefined) {
    return DEFAULT_FOLDER_LIST_PARAMS.limit;
  }
  const n = Number(s);
  return Number.isFinite(n)
    ? Math.min(100, Math.max(1, Math.floor(n)))
    : DEFAULT_FOLDER_LIST_PARAMS.limit;
}

/** Exported for URL round-trip coverage. */
export function parseListParams(params: URLSearchParams): ListParamsState {
  const scopeParam = params.get('scope');
  const scope =
    scopeParam === 'private' || scopeParam === 'shared'
      ? scopeParam
      : undefined;
  const typeParam = params.get('type');
  const type =
    typeParam === 'agent' ||
    typeParam === 'document' ||
    typeParam === 'workflow' ||
    typeParam === 'dataset'
      ? typeParam
      : DEFAULT_FOLDER_LIST_PARAMS.type;
  const ownershipParam = params.get('ownership');
  const ownership =
    ownershipParam === 'mine' || ownershipParam === 'shared'
      ? ownershipParam
      : DEFAULT_FOLDER_LIST_PARAMS.ownership;
  const viewParam = params.get('view');
  const view: LibraryViewMode =
    viewParam === 'grid' ? 'grid' : DEFAULT_FOLDER_LIST_PARAMS.view;
  return {
    view,
    page: parsePage(params.get('page') ?? ''),
    limit: parseLimit(params.get('limit') ?? ''),
    search: params.get('q') ?? DEFAULT_FOLDER_LIST_PARAMS.search,
    sort: (() => {
      const v = params.get('sort');
      return v !== null && v !== '' ? v : undefined;
    })(),
    sortDir:
      params.get('sortDir') === 'asc'
        ? 'asc'
        : params.get('sortDir') === 'desc'
        ? 'desc'
        : DEFAULT_FOLDER_LIST_PARAMS.sortDir,
    scope,
    type,
    ownership,
  };
}

/** Exported for URL round-trip coverage. */
export function serializeListParams(
  s: ListParamsState
): Record<string, string> {
  return {
    page: s.page === DEFAULT_FOLDER_LIST_PARAMS.page ? '' : String(s.page),
    limit: String(s.limit),
    q: s.search,
    sort: s.sort ?? '',
    sortDir: s.sortDir === DEFAULT_FOLDER_LIST_PARAMS.sortDir ? '' : s.sortDir,
    /** Empty string removes `scope` from the URL (see UrlParamAdapter.setParams). */
    scope: s.scope === undefined ? '' : s.scope,
    /** Empty string removes the param from the URL when at its default. */
    type: s.type === DEFAULT_FOLDER_LIST_PARAMS.type ? '' : s.type,
    ownership:
      s.ownership === DEFAULT_FOLDER_LIST_PARAMS.ownership ? '' : s.ownership,
    view: s.view === DEFAULT_FOLDER_LIST_PARAMS.view ? '' : s.view,
  };
}

function listParamsEquality(a: ListParamsState, b: ListParamsState): boolean {
  return (
    a.page === b.page &&
    a.limit === b.limit &&
    a.search === b.search &&
    a.sort === b.sort &&
    a.sortDir === b.sortDir &&
    a.scope === b.scope &&
    a.type === b.type &&
    a.ownership === b.ownership &&
    a.view === b.view
  );
}

export type FolderContentsData = {
  items: FolderContentItem[];
  total: number;
  /**
   * The question this data answers: which folder, under which filters.
   *
   * Carried so a reader can tell whether the data in hand belongs to the view
   * it is rendering. A query change takes effect a render before its fetch
   * settles, and until then the page holds the previous answer — which is how
   * the headline count came to be pinned to the number from the view you just
   * left, or from the filter you just switched off, and stayed there until a
   * reload.
   */
  query: FolderContentsQuery;
  folderMeta: { name: string; scope?: 'user' | 'org' | 'external' } | null;
  /**
   * Aggregate for the collapsed "Chats" row in a folder view: the true chat
   * count (independent of the artifact fetch window) and the newest chat's
   * updatedAt for the row timestamp. Null at the top level (chats live in the
   * stream, not the Library root).
   */
  chatSummary?: FolderChatSummary | null;
};

export type FolderContentsPageModelProps = {
  orgId: string;
  queryClient: QueryClient;
  watchablesFactory: WatchablesFactory;
  urlAdapter: UrlParamAdapter;
  initialSearchParams: URLSearchParams;
  client: {
    resource: Pick<TenantedClient['resource'], 'list' | 'get'> & {
      folder: Pick<
        TenantedClient['resource']['folder'],
        'getFolderItems' | 'getLibraryRoot'
      >;
    };
  };
  /** When null/undefined, top-level folder list. When set, contents of that folder. */
  folderId: string | null;
};

export type ResourceUpdatedFilterPayload = {
  resourceType?: string;
  resourceId?: string;
  folderId?: string | null;
};

function isResultLike(
  value: unknown
): value is { isOk: () => boolean; value: unknown } {
  if (value === null || value === undefined || typeof value !== 'object') {
    return false;
  }
  return (
    'isOk' in value &&
    typeof (value as { isOk?: unknown }).isOk === 'function' &&
    'value' in value
  );
}

/**
 * Resource types the top-level Library actually lists: root folders plus
 * unfiled artifacts of every non-chat type ("All" view) or a single artifact
 * type. A create/archive/move of any of these must refresh the Library; chats
 * are the firehose and never surface in the Library root, so they're excluded.
 */
const TOP_LEVEL_LIBRARY_RESOURCE_TYPES: ReadonlySet<string> = new Set(
  ALL_RESOURCE_KINDS.filter((kind) => kind.capabilities.showsInLibraryRoot).map(
    (kind) => kind.type
  )
);

export function shouldRefreshFolderContentsOnResourceUpdated(params: {
  currentFolderId: string | null;
  payload: ResourceUpdatedFilterPayload | undefined;
}): boolean {
  const { currentFolderId, payload } = params;

  if (!Assert.exists(currentFolderId)) {
    const resourceType = payload?.resourceType;
    return (
      resourceType !== undefined &&
      TOP_LEVEL_LIBRARY_RESOURCE_TYPES.has(resourceType)
    );
  }

  if (payload === undefined) {
    return false;
  }

  const updatesItemWithinCurrentFolder = payload.folderId === currentFolderId;
  const updatesCurrentFolderMeta =
    payload.resourceType === ResourceType.Folder &&
    payload.resourceId === currentFolderId;

  // Client-emitted broadcasts (archive, rename, create) are not augmented with
  // the server's `folderId` the way websocket events are, so when viewing a
  // folder we can't tell from such a payload whether the changed resource lives
  // here. Refresh conservatively whenever a resource event arrives with a
  // resource id but no `folderId` at all — this is what drops an archived row
  // out of an open folder without a manual refresh. Server events carry a
  // `folderId` (an id, or `null` for unfiled), so this arm never fires for
  // them, and a mismatched folder id still falls through to `false`.
  const isUnscopedClientEvent =
    payload.folderId === undefined &&
    payload.resourceId !== undefined &&
    payload.resourceId !== '';

  return (
    updatesItemWithinCurrentFolder ||
    updatesCurrentFolderMeta ||
    isUnscopedClientEvent
  );
}

function folderContentsDataEquality(
  a: FolderContentsData | null,
  b: FolderContentsData | null
): boolean {
  if (a === null || b === null) {
    return a === b;
  }
  if (
    a.total !== b.total ||
    folderContentsQueryKey(a.query) !== folderContentsQueryKey(b.query) ||
    a.items.length !== b.items.length ||
    (a.folderMeta?.name ?? null) !== (b.folderMeta?.name ?? null) ||
    (a.folderMeta?.scope ?? null) !== (b.folderMeta?.scope ?? null) ||
    (a.chatSummary?.count ?? null) !== (b.chatSummary?.count ?? null) ||
    (a.chatSummary?.newestUpdatedAt ?? null) !==
      (b.chatSummary?.newestUpdatedAt ?? null)
  ) {
    return false;
  }
  for (let i = 0; i < a.items.length; i++) {
    const itemA = a.items[i];
    const itemB = b.items[i];
    if (
      itemA === undefined ||
      itemB === undefined ||
      getFolderContentItemId(itemA) !== getFolderContentItemId(itemB)
    ) {
      return false;
    }
  }
  return true;
}

/** Filter data to exclude items whose ids are in removedIds; used for equality that ignores optimistically removed items. */
function filterDataByRemovedIds(
  data: FolderContentsData,
  removedIds: ReadonlySet<string>
): FolderContentsData {
  if (removedIds.size === 0) {
    return data;
  }
  const filteredItems = data.items.filter(
    (item) => !removedIds.has(getFolderContentItemId(item))
  );
  return {
    ...data,
    items: filteredItems,
    total: Math.max(0, data.total - (data.items.length - filteredItems.length)),
  };
}

function folderContentsDataEqualityConsideringRemoved(
  a: FolderContentsData | null,
  b: FolderContentsData | null,
  removedIds: ReadonlySet<string>
): boolean {
  if (a === null || b === null) {
    return a === b;
  }
  if (removedIds.size === 0) {
    return folderContentsDataEquality(a, b);
  }
  const aFiltered = filterDataByRemovedIds(a, removedIds);
  const bFiltered = filterDataByRemovedIds(b, removedIds);
  return folderContentsDataEquality(aFiltered, bFiltered);
}

/**
 * Fetches a single folder's contents for display. Artifacts (every non-chat
 * type) and the chat summary are fetched SEPARATELY so a chat-heavy folder can
 * never push agents/documents/workflows out of the artifact page window, and so
 * the collapsed "Chats" row reflects only what its click-through destination
 * (the folder chat history — user-initiated chats) actually shows. See COT-6561.
 */
/** Resource types a folder view lists for a given type filter. */
export function folderItemTypesForFilter(
  type: ListParamsState['type']
): ResourceType[] {
  switch (type) {
    case 'all':
      return NON_CHAT_FOLDER_CONTENT_TYPES;
    case 'agent':
      return [ResourceType.Agent];
    case 'document':
      return [ResourceType.Document];
    case 'workflow':
      return [ResourceType.Workflow];
    case 'dataset':
      return [ResourceType.Dataset];
    default:
      return Assert.unreachable(type);
  }
}

/**
 * A contents fetch before it is stamped with the query it answered.
 *
 * The stamp is applied in one place — where the params are read — so a fetcher
 * cannot claim to have answered a question it did not ask.
 */
export type FolderContentsFetchResult = Omit<FolderContentsData, 'query'>;

export async function fetchFolderContentsForFolder(params: {
  orgId: string;
  folderId: string;
  scope: 'private' | 'shared' | undefined;
  search: string;
  sort: string | undefined;
  sortDir: 'asc' | 'desc';
  limit: number;
  /**
   * 1-based page to display *through*, not a single slice. Every page from 1
   * to `page` is fetched and concatenated, so "Load more" GROWS the list —
   * matching the top-level Library path. Returning only page N instead would
   * swap the visible rows for a later slice while `total` still exceeded the
   * one loaded page, leaving "Load more" permanently armed.
   */
  page: number;
  /**
   * Narrows the folder's contents to one artifact type (server-side).
   * Defaults to the unfiltered view.
   */
  type?: ListParamsState['type'];
  queryClient: QueryClient;
  client: FolderContentsPageModelProps['client'];
}): Promise<FolderContentsFetchResult> {
  const {
    orgId,
    folderId,
    scope,
    search,
    sort,
    sortDir,
    limit,
    page,
    type = 'all',
    queryClient,
    client,
  } = params;
  const searchParam = search !== '' ? search : undefined;
  const itemTypes = folderItemTypesForFilter(type);
  // The collapsed "Chats" row belongs to the unfiltered view only — under a
  // type filter it would be the one row that ignores the filter.
  const includeChatSummary = type === 'all';
  // Fetch the individually-listed artifacts (everything except chats). Chats are
  // excluded here so a folder with many chats can't push agents/documents/
  // workflows out of the fetch window (COT-6561).
  // One key per page offset, so each page caches independently and re-reading
  // pages 1..N-1 on a "Load more" is served from cache rather than refetched.
  const artifactItemsKeyFor = (offset: number) =>
    queryKeys.resource.folderContents({
      orgId,
      folderId,
      ...(scope !== undefined && { folderScope: scope }),
      itemTypes,
      search: searchParam,
      sort,
      sortDir,
      limit,
      offset,
    });
  const pageOffsets = Array.from(
    { length: Math.max(1, page) },
    (_, i) => i * limit
  );
  const chatSummary = folderChatSummaryQuery({
    orgId,
    folderId,
    scope,
    search: searchParam,
  });

  // The chat summary and the folder record are offset-independent, so they are
  // fetched once no matter how many artifact pages are loaded.
  const [artifactPages, chatOut, folderRes] = await Promise.all([
    Promise.all(
      pageOffsets.map((offset) =>
        queryClient.fetchQuery({
          queryKey: artifactItemsKeyFor(offset),
          staleTime: RESOURCE_LIST_FETCH_STALE_MS,
          queryFn: async () =>
            Assert.assertOk(
              await client.resource.folder.getFolderItems({
                folderId,
                folderScope: scope,
                itemTypes,
                search: searchParam,
                sort,
                sortDir,
                limit,
                offset,
              })
            ),
        })
      )
    ),
    includeChatSummary
      ? queryClient.fetchQuery({
          queryKey: chatSummary.queryKey,
          staleTime: RESOURCE_LIST_FETCH_STALE_MS,
          queryFn: async () =>
            Assert.assertOk(
              await client.resource.folder.getFolderItems(chatSummary.request)
            ),
        })
      : Promise.resolve(null),
    queryClient.fetchQuery({
      queryKey: queryKeys.resource.get({
        orgId,
        type: ResourceType.Folder,
        id: folderId,
      }),
      staleTime: RESOURCE_LIST_FETCH_STALE_MS,
      queryFn: () =>
        client.resource.get({
          type: ResourceType.Folder,
          id: folderId,
        }),
    }),
  ]);
  const folderMeta = (() => {
    if (isResultLike(folderRes)) {
      if (
        folderRes.isOk() &&
        folderRes.value !== null &&
        folderRes.value !== undefined &&
        typeof folderRes.value === 'object' &&
        'type' in folderRes.value &&
        (folderRes.value as { type: string }).type === ResourceType.Folder
      ) {
        const folder = (
          folderRes.value as {
            folder: {
              name: string;
              scope?: 'user' | 'org' | 'external';
            };
          }
        ).folder;
        return {
          name: folder.name,
          scope: folder.scope,
        };
      }
      return null;
    }
    if (
      folderRes !== null &&
      folderRes !== undefined &&
      typeof folderRes === 'object' &&
      'name' in folderRes
    ) {
      const folder = folderRes as {
        name: string;
        scope?: 'user' | 'org' | 'external';
      };
      return {
        name: folder.name,
        scope: folder.scope,
      };
    }
    return null;
  })();
  return {
    items: artifactPages.flatMap((p) => p.items),
    // The server returns the full filtered size on every page, so the last page
    // read is as good as the first — and matches how the top-level path reads it.
    total: artifactPages[artifactPages.length - 1]?.total ?? 0,
    folderMeta,
    chatSummary: chatOut === null ? null : buildChatSummary(chatOut),
  };
}

/**
 * The one definition of a folder's collapsed "Chats" row lookup.
 *
 * A 1-item read: the response `total` is the true count and the single item is
 * the newest chat, so the row gets its count and timestamp without shipping
 * every chat to the client.
 *
 * Shared by the folder page and by inline folder expansion, which MUST agree —
 * the count has to match the folder chat history the row opens into, so it
 * carries that view's `triggerSources` (user-initiated chats only; otherwise
 * agent and trigger runs inflate the row to "99 chats" over a list showing 0,
 * COT-6561). Sharing the key also means either surface warms the other's cache.
 */
export function folderChatSummaryQuery(params: {
  orgId: string;
  folderId: string;
  scope: 'private' | 'shared' | undefined;
  search: string | undefined;
}): {
  queryKey: ReturnType<typeof queryKeys.resource.folderContents>;
  request: {
    folderId: string;
    folderScope: 'private' | 'shared' | undefined;
    itemTypes: ResourceType[];
    search: string | undefined;
    sort: string;
    sortDir: 'asc' | 'desc';
    limit: number;
    offset: number;
    triggerSources: typeof DEFAULT_CHAT_HISTORY_TRIGGER_SOURCES;
  };
} {
  const { orgId, folderId, scope, search } = params;
  const request = {
    folderId,
    folderScope: scope,
    itemTypes: [ResourceType.Chat],
    search,
    sort: 'updatedAt',
    sortDir: 'desc' as const,
    limit: 1,
    offset: 0,
    triggerSources: DEFAULT_CHAT_HISTORY_TRIGGER_SOURCES,
  };
  return {
    queryKey: queryKeys.resource.folderContents({
      orgId,
      folderId,
      // Spread rather than passed through: the key builder distinguishes an
      // absent scope from one explicitly set, where the request does not.
      ...(scope !== undefined && { folderScope: scope }),
      itemTypes: request.itemTypes,
      search,
      sort: request.sort,
      sortDir: request.sortDir,
      limit: request.limit,
      offset: request.offset,
      triggerSources: request.triggerSources,
    }),
    request,
  };
}

/**
 * Derives the collapsed "Chats" row aggregate from the chat-summary response:
 * the true (trigger-source-filtered) count and the newest chat's updatedAt for
 * the row timestamp. Exported for regression coverage (COT-6561).
 */
export function buildChatSummary(chatOut: {
  items: FolderContentItem[];
  total: number;
}): FolderChatSummary {
  const newestChatItem = chatOut.items[0];
  return {
    count: chatOut.total,
    newestUpdatedAt:
      newestChatItem !== undefined && newestChatItem.type === 'chat'
        ? String(newestChatItem.data.updatedAt)
        : null,
  };
}

/**
 * Model for the folder contents page. Owns URL-synced list params and an event
 * watchable that fetches either the top-level folder list or a folder's contents,
 * and refetches on ResourceUpdated (resourceType folder).
 */
/** Human-readable message for a failed contents fetch. */
function loadErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message !== '') {
    return error.message;
  }
  return "We couldn't load this list.";
}

export class FolderContentsPageModel
  extends BaseModel
  implements TargetableModel
{
  private readonly _listParamsUrl: MultiKeyUrlParamWatchable<ListParamsState>;
  private readonly _listParams: ReadonlyWatchable<ListParamsState>;
  /**
   * What the search box renders, updated on every keystroke. Held apart from the
   * committed `search` param so the input stays responsive while the expensive
   * half (fetch + URL rewrite) trails on `_debouncedSearchDraft`.
   */
  private readonly _searchDraft: Watchable<string>;
  private readonly _debouncedSearchDraft: ReadonlyDebouncedWatchable<string>;
  private readonly _unsubSearchCommit: () => void;
  private readonly _folderContentsDataEventWatchable: ReadonlyEventWatchable<FolderContentsData | null>;
  private readonly _optimisticallyRemovedIds: Watchable<ReadonlySet<string>>;
  /**
   * Last fetch failure, or null. The event watchable swallows rejections and
   * keeps its previous value, so without capturing the error here a failed load
   * is indistinguishable from a slow one and the page skeletons forever.
   */
  private readonly _loadError: Watchable<string | null>;
  /**
   * A fetch is in flight whose result REPLACES what is on screen — a new
   * search, sort, type/ownership filter, or a retry. Drives the "results are
   * stale" treatment.
   *
   * Two kinds of refetch are deliberately excluded, because in both the rows
   * already rendered are still correct:
   *
   * - Background self-heal off a `ResourceUpdated` broadcast. It re-runs the
   *   SAME query, and the top-level filter matches any folder/agent/document/
   *   workflow/dataset event anywhere in the org (a folder view matches every
   *   event inside it, a streaming chat included) — so ordinary activity
   *   elsewhere dimmed the whole Library mid-browse. With
   *   `invalidatePrefixesOnEveryRefresh` forcing a real round trip each time,
   *   that read as the page reloading itself every few seconds.
   * - "Load more", which APPENDS. It refetches pages 1..N to rebuild the list,
   *   but the rows on screen are a prefix of the result; the list already has
   *   its own `isLoadingMore` affordance for this.
   *
   * The treatment sets `pointer-events-none` as well as dimming, so every
   * spurious one also ate clicks for the length of a network round trip.
   */
  private readonly _isFetching: Watchable<boolean>;
  /**
   * In-flight count for `_isFetching`, so an overlapping refetch settling first
   * can't clear a treatment another one still owns.
   */
  private _pendingStaleFetches: number;
  private readonly _folderId: string | null;
  private _isTargeted: boolean;

  constructor(props: FolderContentsPageModelProps) {
    super('folder-contents-page');
    this._folderId = props.folderId;
    this._isTargeted = Assert.exists(props.folderId);
    this._loadError = Watchable.fromValue<string | null>(null);
    // Starts false: the first load renders as a skeleton (`status === 'loading'`,
    // keyed off `folderContentsData === null`), not as stale rows, and the
    // mount refresh deliberately does not claim staleness either.
    this._isFetching = Watchable.fromValue<boolean>(false);
    this._pendingStaleFetches = 0;
    this._optimisticallyRemovedIds = Watchable.fromValue<ReadonlySet<string>>(
      new Set(),
      { equalityFn: () => false }
    );
    const initial = parseListParams(props.initialSearchParams);
    const adapter = props.urlAdapter;

    this._listParamsUrl = new MultiKeyUrlParamWatchable<ListParamsState>(
      [
        'page',
        'limit',
        'q',
        'search',
        'sort',
        'sortDir',
        'scope',
        'type',
        'ownership',
        'view',
      ],
      {
        default: DEFAULT_FOLDER_LIST_PARAMS,
        parse: parseListParams,
        serialize: serializeListParams,
        equalityFn: listParamsEquality,
      },
      adapter,
      initial
    );

    this._listParams = this._listParamsUrl.asWatchable();

    this._searchDraft = Watchable.fromValue<string>(initial.search, {
      equalityFn: (a, b) => a === b,
    });
    this._debouncedSearchDraft = DebouncedWatchable.for(this._searchDraft, {
      delayMs: SEARCH_DEBOUNCE_MS,
      equalityFn: (a, b) => a === b,
    });
    this._unsubSearchCommit = this._debouncedSearchDraft.subscribe((search) => {
      // A settled draft that already matches the committed param has nothing to
      // commit. That is the URL-restore path (`syncFromUrl` adopts the draft
      // into the box), where committing would reset `page` to 1 and throw away
      // the pages the restored URL asked for.
      if (search === this._listParamsUrl.snapshot().search) {
        return;
      }
      this.setSearch(search);
    });

    const folderKeyId = props.folderId;

    const watchablesFactory = props.watchablesFactory;

    this._folderContentsDataEventWatchable =
      watchablesFactory.makeFolderContentsWatchable<
        FolderContentsData | null,
        ResourceUpdatedFilterPayload
      >({
        orgId: props.orgId,
        folderId: folderKeyId,
        getScope: (get) => get(this._listParams).scope,
        initialValue: null,
        equalityFn: (a, b) =>
          folderContentsDataEqualityConsideringRemoved(
            a,
            b,
            this._optimisticallyRemovedIds.snapshot()
          ),
        filter: (payload) => {
          return shouldRefreshFolderContentsOnResourceUpdated({
            currentFolderId: this._folderId,
            payload,
          });
        },
        fn: async (_payload, get) => {
          // The stamp is read here, alongside the fetch it describes, so the
          // data that comes back says which query it answered rather than
          // being assumed to answer the one on screen when it lands.
          const { type, ownership, search, scope } = get(this._listParams);
          const query: FolderContentsQuery = {
            folderId: this._folderId,
            type,
            ownership,
            search,
            scope: scope ?? null,
          };
          try {
            const value = await this._fetchContents(get, props);
            this._loadError.set(null);
            return { ...value, query };
          } catch (error) {
            // Rethrow so the watchable keeps the last good page instead of
            // blanking the list; the page reads `loadErrorWatchable` to decide
            // between "retry" and "stale but usable".
            this._loadError.set(loadErrorMessage(error));
            throw error;
          }
        },
      });
  }

  /** The actual contents fetch, split out so `fn` can own error/loading state. */
  private async _fetchContents(
    get: <U>(w: ReadonlyWatchable<U>) => U,
    props: FolderContentsPageModelProps
  ): Promise<FolderContentsFetchResult> {
    const { page, limit, search, sort, sortDir, scope, type, ownership } = get(
      this._listParams
    );
    const folderId = this._folderId;
    const isTopLevel = !Assert.exists(folderId);

    if (isTopLevel) {
      // The Library filters are SERVER-side (ownership/type/search/sort/
      // paging). "Load more" bumps `page`; we fetch pages 1..page (each
      // cached by fetchQuery) and concat so the visible list GROWS instead
      // of jumping to a single later page. Server order (folders-first in
      // the "All" view) is preserved across the concatenation.
      const ownershipParam = ownership === 'all' ? undefined : ownership;
      const searchParam = search !== '' ? search : undefined;
      const pageOffsets = Array.from({ length: page }, (_, i) => i * limit);

      if (type === 'all') {
        // "All" = root folders (float to the top) + unfiled artifacts of
        // every non-chat type. Chats are the firehose — they live in the
        // stream, so `library-root` intentionally excludes them.
        const pages = await Promise.all(
          pageOffsets.map((pageOffset) =>
            props.queryClient.fetchQuery({
              queryKey: queryKeys.resource.libraryRoot({
                orgId: props.orgId,
                ownership: ownershipParam,
                search: searchParam,
                sort,
                sortDir,
                limit,
                offset: pageOffset,
              }),
              staleTime: RESOURCE_LIST_FETCH_STALE_MS,
              queryFn: async () =>
                Assert.assertOk(
                  await props.client.resource.folder.getLibraryRoot({
                    ownership: ownershipParam,
                    search: searchParam,
                    sort,
                    sortDir,
                    limit,
                    offset: pageOffset,
                  })
                ),
            })
          )
        );
        return {
          items: pages.flatMap((p) => p.items),
          total: pages[pages.length - 1]?.total ?? 0,
          folderMeta: null,
        };
      }

      // Single-type view: every artifact of that type (filed + unfiled),
      // ownership-filtered and paginated by the server.
      const resourceType =
        type === 'agent'
          ? ResourceType.Agent
          : type === 'document'
          ? ResourceType.Document
          : type === 'dataset'
          ? ResourceType.Dataset
          : ResourceType.Workflow;
      const pages = await Promise.all(
        pageOffsets.map((pageOffset) =>
          props.queryClient.fetchQuery({
            queryKey: queryKeys.resource.listPage({
              orgId: props.orgId,
              type: resourceType,
              limit,
              offset: pageOffset,
              ...(searchParam !== undefined && { search: searchParam }),
              ...(sort !== undefined && { sort }),
              sortDir,
              ...(scope !== undefined && { folderScope: scope }),
              ...(ownershipParam !== undefined && {
                ownership: ownershipParam,
              }),
            }),
            staleTime: RESOURCE_LIST_FETCH_STALE_MS,
            queryFn: async () =>
              Assert.assertOk(
                await props.client.resource.list({
                  type: resourceType,
                  limit,
                  offset: pageOffset,
                  search: searchParam,
                  sort,
                  sortDir,
                  folderScope: scope,
                  ownership: ownershipParam,
                })
              ),
          })
        )
      );
      const items: FolderContentItem[] = pages.flatMap(
        (out): FolderContentItem[] => {
          switch (out.type) {
            case ResourceType.Agent:
              return out.items.map((data) => ({
                type: 'agent' as const,
                data,
              }));
            case ResourceType.Document:
              return out.items.map((data) => ({
                type: 'document' as const,
                data,
              }));
            case ResourceType.Workflow:
              return out.items.map((data) => ({
                type: 'workflow' as const,
                data,
              }));
            case ResourceType.Dataset:
              return out.items.map((data) => ({
                type: 'dataset' as const,
                data,
              }));
            default:
              return [];
          }
        }
      );
      return {
        items,
        total: pages[pages.length - 1]?.total ?? 0,
        folderMeta: null,
      };
    }

    // Nested folders accumulate pages 1..page exactly like the top-level lists
    // above, so "Load more" inside a folder appends rather than replaces.
    return fetchFolderContentsForFolder({
      orgId: props.orgId,
      folderId,
      scope,
      search,
      sort,
      sortDir,
      limit,
      page,
      type,
      queryClient: props.queryClient,
      client: props.client,
    });
  }

  get listParamsWatchable(): ReadonlyWatchable<ListParamsState> {
    return this._listParams;
  }

  /** The live search box text (every keystroke), not the committed `search` param. */
  get searchDraftWatchable(): ReadonlyWatchable<string> {
    return this._searchDraft;
  }

  /**
   * Records a keystroke in the search box. The commit (fetch + URL rewrite)
   * trails on a debounce; nothing else needs to be scheduled by the caller.
   */
  setSearchDraft(search: string): void {
    this._searchDraft.set(search);
  }

  get folderContentsDataWatchable(): ReadonlyEventWatchable<FolderContentsData | null> {
    return this._folderContentsDataEventWatchable;
  }

  /** IDs of items optimistically removed from the list (e.g. during move). Used by the page to filter display. */
  get optimisticallyRemovedIdsWatchable(): ReadonlyWatchable<
    ReadonlySet<string>
  > {
    return this._optimisticallyRemovedIds;
  }

  /** Message from the last failed fetch, or null when the last fetch succeeded. */
  get loadErrorWatchable(): ReadonlyWatchable<string | null> {
    return this._loadError;
  }

  /**
   * Whether a fetch the user is waiting on is in flight — a param change or a
   * retry, not a background refresh for the query already on screen.
   */
  get isFetchingWatchable(): ReadonlyWatchable<boolean> {
    return this._isFetching;
  }

  /** Clears the error and refetches. Backs the "Try again" button. */
  retry(): void {
    this._loadError.set(null);
    this.refresh();
  }

  /**
   * Refetches the current params and marks the rows on screen stale for the
   * duration — they belong to the previous query.
   *
   * Rejections are already captured into `loadErrorWatchable` by `fn`, so
   * they're swallowed here rather than surfacing as unhandled promise
   * rejections.
   */
  refresh(): void {
    // Flagged here rather than inside the watchable's `fn`. `fn` could infer the
    // trigger (an event-driven refresh carries a payload, mount and manual ones
    // don't), but it runs a tick later — behind the prefix invalidation — and it
    // cannot tell a param change from "load more". Deciding at the call site
    // puts the choice where the reason is known, and makes it synchronous with
    // the click.
    this._pendingStaleFetches += 1;
    this._isFetching.set(true);
    void this._folderContentsDataEventWatchable
      .refresh()
      .catch(() => {
        // Reported via `loadErrorWatchable`.
      })
      .finally(() => {
        this._pendingStaleFetches -= 1;
        this._isFetching.set(this._pendingStaleFetches > 0);
      });
  }

  /**
   * Refetches without the stale treatment, for a change that only GROWS the
   * list ("load more"). The visible rows stay valid, so dimming them — and
   * disabling their clicks — for the round trip is wrong; the list's own
   * `isLoadingMore` row covers it.
   */
  private refreshAppending(): void {
    void this._folderContentsDataEventWatchable.refresh().catch(() => {
      // Reported via `loadErrorWatchable`.
    });
  }

  /** Resets every filter (type, ownership, search) back to its default. */
  clearFilters(): void {
    const current = this._listParamsUrl.snapshot();
    this.updateListParams({
      ...current,
      type: DEFAULT_FOLDER_LIST_PARAMS.type,
      ownership: DEFAULT_FOLDER_LIST_PARAMS.ownership,
      search: DEFAULT_FOLDER_LIST_PARAMS.search,
      page: 1,
    });
    // Empty the box too, otherwise it keeps displaying a query that no longer
    // filters anything. Any in-flight debounce resolves to this same value.
    this._searchDraft.set(DEFAULT_FOLDER_LIST_PARAMS.search);
  }

  /**
   * Optimistically removes items from the folder contents list by ID.
   * Returns a rollback function that restores the previous state.
   */
  removeItemsOptimistically(ids: string[]): () => void {
    const previous = this._optimisticallyRemovedIds.snapshot();
    this._optimisticallyRemovedIds.set(new Set([...previous, ...ids]));
    return () => {
      this._optimisticallyRemovedIds.set(previous);
    };
  }

  get isTopLevel(): boolean {
    return !Assert.exists(this._folderId);
  }

  get folderId(): string | null {
    return this._folderId;
  }

  /** Sync list params from URL and refetch folder contents for the current params. */
  syncFromUrl(searchParams: URLSearchParams): void {
    const before = this._listParamsUrl.snapshot();
    this._listParamsUrl.syncFromUrl(searchParams);
    const after = this._listParamsUrl.snapshot();
    if (listParamsEquality(before, after)) {
      return;
    }
    if (after.search !== before.search) {
      // Back/forward or an inbound link moved the query out from under the box:
      // adopt it. The trailing commit then no-ops, since the draft now matches
      // the committed param. Our own commits never land here — they write
      // `_listParamsUrl` first, so `before` already holds the new value.
      this._searchDraft.set(after.search);
    }
    this.refresh();
  }

  /**
   * Set list params to the given page and take. Backs "load more", which
   * appends: `_fetchContents` refetches pages 1..page and concatenates, so the
   * rows already on screen are a prefix of the result and must not be marked
   * stale.
   */
  loadPage(page: number, take: number): void {
    const current = this._listParamsUrl.snapshot();
    this.updateListParams({ ...current, page, limit: take }, { append: true });
  }

  /** Set search query; resets page to 1. */
  setSearch(search: string): void {
    const current = this._listParamsUrl.snapshot();
    this.updateListParams({ ...current, search, page: 1 });
  }

  /** Set sort key and direction; resets page to 1. */
  setSort(sort: string | undefined, sortDir: 'asc' | 'desc'): void {
    const current = this._listParamsUrl.snapshot();
    this.updateListParams({ ...current, sort, sortDir, page: 1 });
  }

  /** Set scope filter (all/private/shared); resets page to 1. */
  setScope(scope: 'private' | 'shared' | undefined): void {
    const current = this._listParamsUrl.snapshot();
    this.updateListParams({ ...current, scope, page: 1 });
  }

  /** Set the Library type filter (all/agent/document/workflow); resets page to 1. */
  setTypeFilter(type: ListParamsState['type']): void {
    const current = this._listParamsUrl.snapshot();
    this.updateListParams({ ...current, type, page: 1 });
  }

  /** Set the ownership lens (all/mine/shared); resets page to 1. */
  setOwnership(ownership: ListParamsState['ownership']): void {
    const current = this._listParamsUrl.snapshot();
    this.updateListParams({ ...current, ownership, page: 1 });
  }

  /**
   * Switch rows/cards. Deliberately does NOT reset paging or refetch — the view
   * mode is presentation only; the same items render either way.
   */
  setViewMode(view: LibraryViewMode): void {
    const current = this._listParamsUrl.snapshot();
    if (current.view === view) {
      return;
    }
    this._listParamsUrl.set({ ...current, view });
  }

  private updateListParams(
    next: ListParamsState,
    opts: { append?: boolean } = {}
  ): void {
    const current = this._listParamsUrl.snapshot();
    if (listParamsEquality(current, next)) {
      return;
    }
    this._listParamsUrl.set(next);
    if (opts.append === true) {
      this.refreshAppending();
      return;
    }
    this.refresh();
  }

  dispose(): void {
    this._unsubSearchCommit();
    this._debouncedSearchDraft.unsubscribe();
    this._folderContentsDataEventWatchable.unsubscribe();
    this._listParamsUrl.dispose();
  }

  get isTargeted(): boolean {
    return this._isTargeted;
  }

  target(): void {
    this._isTargeted = true;
  }

  markAsNotTargeted(): void {
    this._isTargeted = false;
  }
}

export const useFolderContentsPageModel = (): FolderContentsPageModel => {
  const model = useInScopeModel(FolderContentsPageModel);
  if (model === null) {
    throw new Error('FolderContentsPageModel not found in scope');
  }
  return model;
};
